import type { AccidentalSpelling, LiveComposerEvent, StaffNumber } from '../../CreateMusicXml';

const MIN_EVENT_DURATION_DIVISIONS = 0.25;
const STAFF_SPLIT_MIDI = 60;

export type JoinableComposerEvent = LiveComposerEvent & {
  startDivisions: number;
  durationDivisions: number;
};

export type JoinSelectionPlan =
  | {
      enabled: true;
      mode: 'sustain' | 'chord';
      reason: string;
      events: LiveComposerEvent[];
      selectedEventId: string;
    }
  | {
      enabled: false;
      reason: string;
      events: LiveComposerEvent[];
      selectedEventId?: undefined;
      mode?: undefined;
    };

const normalizeMidiNotes = (midiNotes: number[]) =>
  Array.from(new Set(midiNotes.map((midi) => Math.max(0, Math.min(127, Math.round(midi)))))).sort(
    (a, b) => a - b,
  );

const getDraftEventStartDivisions = (event: LiveComposerEvent, fallbackCursorDivisions: number) =>
  event.startDivisions == null
    ? fallbackCursorDivisions + Math.max(0, event.restBeforeDivisions)
    : Math.max(0, event.startDivisions);

const getEventSortMidi = (event: Pick<LiveComposerEvent, 'midiNotes' | 'staff'>) => {
  const firstMidi = normalizeMidiNotes(event.midiNotes)[0];
  if (firstMidi != null) return firstMidi;
  if (event.staff === 2) return STAFF_SPLIT_MIDI - 1;
  return STAFF_SPLIT_MIDI;
};

export const getJoinableTimelineEvents = (events: LiveComposerEvent[]): JoinableComposerEvent[] => {
  let cursorDivisions = 0;

  return events
    .map((event) => {
      const durationDivisions = Math.max(MIN_EVENT_DURATION_DIVISIONS, event.durationDivisions);
      const startDivisions = getDraftEventStartDivisions(event, cursorDivisions);
      cursorDivisions =
        event.startDivisions == null
          ? cursorDivisions + Math.max(0, event.restBeforeDivisions) + durationDivisions
          : Math.max(cursorDivisions, startDivisions + durationDivisions);

      return {
        ...event,
        midiNotes: normalizeMidiNotes(event.midiNotes),
        startDivisions,
        durationDivisions,
      };
    })
    .sort(
      (a, b) =>
        a.startDivisions - b.startDivisions ||
        a.durationDivisions - b.durationDivisions ||
        getEventSortMidi(a) - getEventSortMidi(b),
    );
};

const sortComposerEvents = (events: LiveComposerEvent[]) =>
  getJoinableTimelineEvents(events).map((event) => ({
    ...event,
    restBeforeDivisions: event.startDivisions,
  }));

const isRestEvent = (event: Pick<LiveComposerEvent, 'midiNotes'>) =>
  normalizeMidiNotes(event.midiNotes).length === 0;

const getAutoStaffForNotes = (event: Pick<LiveComposerEvent, 'midiNotes'>): StaffNumber | null => {
  const notes = normalizeMidiNotes(event.midiNotes);
  if (notes.length === 0) return null;
  if (notes.every((midi) => midi >= STAFF_SPLIT_MIDI)) return 1;
  if (notes.every((midi) => midi < STAFF_SPLIT_MIDI)) return 2;
  return null;
};

const resolveJoinStaff = (
  events: Array<Pick<LiveComposerEvent, 'midiNotes' | 'staff'>>,
): { ok: true; staff?: StaffNumber } | { ok: false; reason: string } => {
  const explicitStaffs = new Set<StaffNumber>();

  events.forEach((event) => {
    if (event.staff === 1 || event.staff === 2) {
      explicitStaffs.add(event.staff);
    }
  });

  if (explicitStaffs.size > 1) {
    return { ok: false, reason: 'Selected events use different hands.' };
  }

  if (explicitStaffs.size === 0) {
    return { ok: true, staff: undefined };
  }

  const staff = Array.from(explicitStaffs)[0];
  const ambiguousAutoEvent = events.find((event) => {
    if (event.staff === staff) return false;
    const inferredStaff = getAutoStaffForNotes(event);
    return inferredStaff !== staff;
  });

  if (ambiguousAutoEvent) {
    return { ok: false, reason: 'Selected events mix explicit and automatic hand assignment.' };
  }

  return { ok: true, staff };
};

const noteSetKey = (event: Pick<LiveComposerEvent, 'midiNotes'>) =>
  normalizeMidiNotes(event.midiNotes).join(',');

const pickAccidentalSpellings = (
  events: JoinableComposerEvent[],
  midiNotes: number[],
): Record<number, AccidentalSpelling> | undefined => {
  const nextSpellings: Record<number, AccidentalSpelling> = {};

  normalizeMidiNotes(midiNotes).forEach((midi) => {
    const firstSpelling = events
      .map((event) => event.accidentalSpellings?.[midi])
      .find((spelling): spelling is AccidentalSpelling => Boolean(spelling && spelling !== 'auto'));

    if (firstSpelling) {
      nextSpellings[midi] = firstSpelling;
    }
  });

  return Object.keys(nextSpellings).length > 0 ? nextSpellings : undefined;
};

const freezeJoinedEventTiming = (
  event: JoinableComposerEvent,
  source: LiveComposerEvent['source'],
): JoinableComposerEvent => ({
  ...event,
  source,
  restBeforeDivisions: 0,
  adaptiveTiming: false,
  rawStartedAtMs: undefined,
  rawEndedAtMs: undefined,
  rawCaptureSessionStartedAtMs: undefined,
  rawCaptureSessionStartDivisions: undefined,
  endedAtMs: undefined,
});

const buildDisabledPlan = (events: LiveComposerEvent[], reason: string): JoinSelectionPlan => ({
  enabled: false,
  reason,
  events,
});

const canMergeAsChord = (selectedEvents: JoinableComposerEvent[]) => {
  if (selectedEvents.length < 2) return false;
  if (selectedEvents.some(isRestEvent)) return false;

  const firstEvent = selectedEvents[0];
  if (
    !selectedEvents.every(
      (event) =>
        event.startDivisions === firstEvent.startDivisions &&
        event.durationDivisions === firstEvent.durationDivisions,
    )
  ) {
    return false;
  }

  const allNotes = selectedEvents.flatMap((event) => normalizeMidiNotes(event.midiNotes));
  return allNotes.length > 1 && new Set(allNotes).size === allNotes.length;
};

const canJoinAsSustain = (selectedEvents: JoinableComposerEvent[]) => {
  if (selectedEvents.length < 2) return false;

  const firstEvent = selectedEvents[0];
  const joiningRests = isRestEvent(firstEvent);
  const expectedNoteKey = noteSetKey(firstEvent);

  if (
    !selectedEvents.every((event) =>
      joiningRests
        ? isRestEvent(event)
        : !isRestEvent(event) && noteSetKey(event) === expectedNoteKey,
    )
  ) {
    return false;
  }

  return selectedEvents
    .slice(1)
    .every(
      (event, index) =>
        event.startDivisions ===
        selectedEvents[index].startDivisions + selectedEvents[index].durationDivisions,
    );
};

const getJoinedSource = (events: JoinableComposerEvent[]): LiveComposerEvent['source'] =>
  events.every((event) => event.source === 'audio') ? 'audio' : 'manual';

const buildJoinedEvent = (
  selectedEvents: JoinableComposerEvent[],
  mode: 'sustain' | 'chord',
  staff: StaffNumber | undefined,
): LiveComposerEvent => {
  const sortedEvents = [...selectedEvents].sort(
    (a, b) =>
      a.startDivisions - b.startDivisions ||
      getEventSortMidi(a) - getEventSortMidi(b) ||
      a.id.localeCompare(b.id),
  );
  const firstEvent = sortedEvents[0];
  const source = getJoinedSource(sortedEvents);
  const normalizedFirstEvent = freezeJoinedEventTiming(firstEvent, source);

  if (mode === 'chord') {
    const midiNotes = normalizeMidiNotes(sortedEvents.flatMap((event) => event.midiNotes));
    return {
      ...normalizedFirstEvent,
      midiNotes,
      accidentalSpellings: pickAccidentalSpellings(sortedEvents, midiNotes),
      startDivisions: firstEvent.startDivisions,
      durationDivisions: firstEvent.durationDivisions,
      staff,
    };
  }

  const startDivisions = Math.min(...sortedEvents.map((event) => event.startDivisions));
  const endDivisions = Math.max(
    ...sortedEvents.map((event) => event.startDivisions + event.durationDivisions),
  );
  const midiNotes = normalizeMidiNotes(firstEvent.midiNotes);

  return {
    ...normalizedFirstEvent,
    midiNotes,
    accidentalSpellings: pickAccidentalSpellings(sortedEvents, midiNotes),
    startDivisions,
    durationDivisions: Math.max(MIN_EVENT_DURATION_DIVISIONS, endDivisions - startDivisions),
    staff,
  };
};

export const getJoinSelectionPlan = (
  events: LiveComposerEvent[],
  selectedEventIds: string[],
): JoinSelectionPlan => {
  const selectedIdSet = new Set(selectedEventIds);
  if (selectedIdSet.size < 2) {
    return buildDisabledPlan(events, 'Select at least two events to join.');
  }

  const timelineEvents = getJoinableTimelineEvents(events);
  const selectedEvents = timelineEvents.filter((event) => selectedIdSet.has(event.id));
  if (selectedEvents.length !== selectedIdSet.size) {
    return buildDisabledPlan(events, 'Selected events are no longer available.');
  }

  const sortedSelection = [...selectedEvents].sort(
    (a, b) =>
      a.startDivisions - b.startDivisions ||
      a.durationDivisions - b.durationDivisions ||
      getEventSortMidi(a) - getEventSortMidi(b),
  );
  const staffResult = resolveJoinStaff(sortedSelection);
  if (!staffResult.ok) {
    return buildDisabledPlan(events, staffResult.reason);
  }

  const mode = canMergeAsChord(sortedSelection)
    ? 'chord'
    : canJoinAsSustain(sortedSelection)
      ? 'sustain'
      : null;

  if (!mode) {
    return buildDisabledPlan(
      events,
      'Join needs adjacent matching notes, adjacent rests, or notes that start together.',
    );
  }

  const joinedEvent = buildJoinedEvent(sortedSelection, mode, staffResult.staff);
  const nextEvents = sortComposerEvents([
    ...events.filter((event) => !selectedIdSet.has(event.id)),
    joinedEvent,
  ]);

  return {
    enabled: true,
    mode,
    reason:
      mode === 'chord'
        ? 'Merge selected notes into a chord.'
        : isRestEvent(sortedSelection[0])
          ? 'Join selected rests into one rest.'
          : 'Tie selected same-pitch notes into one sustained note.',
    events: nextEvents,
    selectedEventId: joinedEvent.id,
  };
};

const hasSameStartAndDuration = (event: JoinableComposerEvent, candidate: JoinableComposerEvent) =>
  event.id !== candidate.id &&
  !isRestEvent(event) &&
  !isRestEvent(candidate) &&
  event.startDivisions === candidate.startDivisions &&
  event.durationDivisions === candidate.durationDivisions;

const hasSameSustainMaterial = (event: JoinableComposerEvent, candidate: JoinableComposerEvent) =>
  isRestEvent(event) === isRestEvent(candidate) &&
  (isRestEvent(candidate) || noteSetKey(event) === noteSetKey(candidate));

const collectAdjacentSustainIds = (
  timelineEvents: JoinableComposerEvent[],
  candidate: JoinableComposerEvent,
) => {
  const previousEvents: JoinableComposerEvent[] = [];
  let cursorStart = candidate.startDivisions;

  while (true) {
    const previous = timelineEvents
      .filter(
        (event) =>
          event.id !== candidate.id &&
          hasSameSustainMaterial(event, candidate) &&
          event.startDivisions + event.durationDivisions === cursorStart,
      )
      .sort((a, b) => b.startDivisions - a.startDivisions)[0];
    if (!previous) break;
    previousEvents.unshift(previous);
    cursorStart = previous.startDivisions;
  }

  const nextEvents: JoinableComposerEvent[] = [];
  let cursorEnd = candidate.startDivisions + candidate.durationDivisions;

  while (true) {
    const next = timelineEvents
      .filter(
        (event) =>
          event.id !== candidate.id &&
          hasSameSustainMaterial(event, candidate) &&
          event.startDivisions === cursorEnd,
      )
      .sort((a, b) => a.startDivisions - b.startDivisions)[0];
    if (!next) break;
    nextEvents.push(next);
    cursorEnd = next.startDivisions + next.durationDivisions;
  }

  return [...previousEvents, candidate, ...nextEvents].map((event) => event.id);
};

export const getDropJoinPlan = (
  events: LiveComposerEvent[],
  candidateEventId: string,
): JoinSelectionPlan => {
  const timelineEvents = getJoinableTimelineEvents(events);
  const candidate = timelineEvents.find((event) => event.id === candidateEventId);
  if (!candidate) {
    return buildDisabledPlan(events, 'Drop target is no longer available.');
  }

  const sameStartSelectionIds = timelineEvents
    .filter((event) => hasSameStartAndDuration(event, candidate))
    .map((event) => event.id)
    .concat(candidate.id);
  const sameStartPlan = getJoinSelectionPlan(events, sameStartSelectionIds);
  if (sameStartPlan.enabled) return sameStartPlan;

  const adjacentSelectionIds = collectAdjacentSustainIds(timelineEvents, candidate);
  if (adjacentSelectionIds.length > 1) {
    return getJoinSelectionPlan(events, adjacentSelectionIds);
  }

  return buildDisabledPlan(events, 'No compatible join target at this drop position.');
};
